import { Modal, ButtonGroup, Button } from '@nike/eds';
import { useState } from 'react';
import { useAtom } from 'jotai';
import { uniqBy } from 'ramda';
import { RANKING_RULE_TARGET_TYPES } from '../../constants/index';
import {
  rankingRuleTargetModalAtom,
  rankingRuleTargetBoosts,
  rankingRuleTargetBuries,
} from '../../atoms/ranking';
import TargetStyleColorForm from './TargetStyleColorForm';
import TargetStyleCodeForm from './TargetStyleCodeForm';
import TargetPBIDForm from './TargetPBIDForm';
import TargetTaxonomyForm from './TargetTaxonomyForm';
import TargetPriceForm from './TargetPriceForm';
import {
  styleCodeValidator,
  styleColorValidator,
  pbidValidator,
  priceValueValidator,
} from '../../utils/rankingRuleHelpers';
import { isExistScope } from '../../utils/authHelpers';
import { useLoaderData } from 'react-router-dom';
import { RankingRuleActionType } from 'types/ranking';
import { ArrowBigDown, ArrowBigUp } from 'lucide-react';

const RANKING_RULE_TARGET_TYPE_NAME = {
  [RANKING_RULE_TARGET_TYPES.STYLE_COLOR]: 'Style Color',
  [RANKING_RULE_TARGET_TYPES.STYLE_CODE]: 'Style Code',
  [RANKING_RULE_TARGET_TYPES.PBID]: 'Pbid',
  [RANKING_RULE_TARGET_TYPES.TAXONOMY]: 'Taxonomy',
  [RANKING_RULE_TARGET_TYPES.PRICE]: 'Price',
};

// Move styleColor/styleCode boosts to the begining of the array
function moveStyleColorStyleCodeBoostsToHead(
  items: RankingRuleTargetBoostItem[],
) {
  const g0: RankingRuleTargetBoostItem[] = [];
  const g1: RankingRuleTargetBoostItem[] = [];
  for (const item of items) {
    if (
      item.type === RANKING_RULE_TARGET_TYPES.STYLE_COLOR ||
      item.type === RANKING_RULE_TARGET_TYPES.STYLE_CODE
    ) {
      g0.push(item);
    } else {
      g1.push(item);
    }
  }
  return g0.concat(g1);
}

function ModalHeader(props: {
  actionType: RankingRuleActionType;
  type: string;
}) {
  return (
    <div className='tw-flex tw-items-center tw-gap-1'>
      <h3 className='tw-font-bold tw-text-xl'>
        {RANKING_RULE_TARGET_TYPE_NAME[props.type]}
      </h3>
      <span className='tw-font-normal tw-text-xl tw-text-gray-600'>
        {props.actionType}
      </span>
      {props.actionType === RankingRuleActionType.Boost ? (
        <ArrowBigUp className='tw-text-green-500' />
      ) : (
        <ArrowBigDown className='tw-text-red-500' />
      )}
    </div>
  );
}

export default function RankingRuleTargetModal() {
  const loaderData = useLoaderData() as { access_token: string };
  const [targetModal, setTargetModal] = useAtom(rankingRuleTargetModalAtom);
  const [targetBoosts, setTargetBoosts] = useAtom(rankingRuleTargetBoosts);
  const [targetBuries, setTargetBuries] = useAtom(rankingRuleTargetBuries);
  const jwt = loaderData.access_token;
  const canWrite = isExistScope('search360:ranking:write', jwt);
  const [formValidationErr, setFormValidationErr] = useState('');
  const resetFormData = () => {
    setTargetBoosts([]);
    setTargetBuries([]);
    setFormValidationErr('');
  };
  const onDismissModal = () => {
    setTargetModal((modalData) => {
      modalData.visible = false;
      modalData.type = '';
    });
    resetFormData();
  };

  const handleBoostsValidation = () => {
    switch (targetModal.type) {
      case RANKING_RULE_TARGET_TYPES.STYLE_COLOR:
        return targetBoosts.every((boost) =>
          styleColorValidator(boost.value as string),
        );
      case RANKING_RULE_TARGET_TYPES.STYLE_CODE:
        return targetBoosts.every((boost) =>
          styleCodeValidator(boost.value as string),
        );
      case RANKING_RULE_TARGET_TYPES.PBID:
        return targetBoosts.every((boost) =>
          pbidValidator(boost.value as string),
        );
      case RANKING_RULE_TARGET_TYPES.TAXONOMY:
        return targetBoosts.every((boost) => !!boost.value);
      case RANKING_RULE_TARGET_TYPES.PRICE:
        return targetBoosts.every((boost: RankingRuleTargetBoostItem) => {
          if (boost.multiValue) {
            return boost.multiValue.every((mv) => priceValueValidator(mv));
          }
          if (boost.value) {
            return priceValueValidator(boost.value);
          }
          return false;
        });
      default:
        return false;
    }
  };
  const handleBuriesValidation = () => {
    switch (targetModal.type) {
      case RANKING_RULE_TARGET_TYPES.STYLE_COLOR:
        return targetBuries.every((bury) =>
          styleColorValidator(bury.value as string),
        );
      case RANKING_RULE_TARGET_TYPES.STYLE_CODE:
        return targetBuries.every((bury) =>
          styleCodeValidator(bury.value as string),
        );
      case RANKING_RULE_TARGET_TYPES.PBID:
        return targetBuries.every((bury) =>
          pbidValidator(bury.value as string),
        );
      case RANKING_RULE_TARGET_TYPES.TAXONOMY:
        return targetBuries.every((bury) => !!bury.value);
      case RANKING_RULE_TARGET_TYPES.PRICE:
        return targetBuries.every((bury: RankingRuleTargetBuryItem) => {
          if (bury.multiValue) {
            return bury.multiValue.every((mv) => priceValueValidator(mv));
          }
          if (bury.value) {
            return priceValueValidator(bury.value);
          }
          return false;
        });
      default:
        return false;
    }
  };

  const onAddBtn = () => {
    const isValid =
      targetModal.actionType === RankingRuleActionType.Boost
        ? handleBoostsValidation()
        : handleBuriesValidation();

    if (!isValid) {
      setFormValidationErr('Please check your input');
      return;
    }
    setTargetModal((modalData) => {
      modalData.visible = false;
      modalData.type = '';
      if (targetModal.actionType === RankingRuleActionType.Boost) {
        const boosts = [...modalData.boosts, ...targetBoosts];
        const dedupedBoosts = uniqBy((b) => JSON.stringify(b), boosts);
        modalData.boosts = moveStyleColorStyleCodeBoostsToHead(dedupedBoosts);
      } else {
        const buries = [...modalData.buries, ...targetBuries];
        const dedupedBuries = uniqBy((b) => JSON.stringify(b), buries);
        modalData.buries = dedupedBuries;
      }
    });

    resetFormData();
  };

  const handleActionItemsChange = (
    items: RankingRuleTargetBoostItem[] | RankingRuleTargetBuryItem,
  ) => {
    if (targetModal.actionType === RankingRuleActionType.Boost) {
      setTargetBoosts(items as RankingRuleTargetBoostItem[]);
      return;
    } else if (targetModal.actionType === RankingRuleActionType.Bury) {
      setTargetBuries(items as RankingRuleTargetBuryItem[]);
      return;
    }
    throw new Error('Unexpected code path');
  };

  return (
    <Modal
      onDismiss={onDismissModal}
      isOpen={targetModal.visible}
      headerSlot={
        <ModalHeader
          actionType={targetModal.actionType}
          type={targetModal.type}
        />
      }
      hideFade
      style={
        {
          // hack: make the gut between the header and content smaller
          '--eds-space-24': '4px',
          // hack: make the out padding of the modal smaller
          '--eds-space-32': '20px',
        } as React.CSSProperties
      }
    >
      <div style={{ width: '40rem' }}>
        {targetModal.type === RANKING_RULE_TARGET_TYPES.STYLE_COLOR && (
          <TargetStyleColorForm
            validationError={formValidationErr}
            onActionItemsChange={handleActionItemsChange}
          />
        )}
        {targetModal.type === RANKING_RULE_TARGET_TYPES.STYLE_CODE && (
          <TargetStyleCodeForm
            validationError={formValidationErr}
            onActionItemsChange={handleActionItemsChange}
          />
        )}
        {targetModal.type === RANKING_RULE_TARGET_TYPES.PBID && (
          <TargetPBIDForm
            validationError={formValidationErr}
            onActionItemsChange={handleActionItemsChange}
          />
        )}
        {targetModal.type === RANKING_RULE_TARGET_TYPES.TAXONOMY && (
          <TargetTaxonomyForm
            validationError={formValidationErr}
            onActionItemsChange={handleActionItemsChange}
          />
        )}
        {targetModal.type === RANKING_RULE_TARGET_TYPES.PRICE && (
          <TargetPriceForm
            validationError={formValidationErr}
            onActionItemsChange={handleActionItemsChange}
          />
        )}
        <ButtonGroup className='tw-mt-3 tw-justify-end'>
          {canWrite && (
            <Button onClick={onAddBtn} variant='primary'>
              Add
            </Button>
          )}
          <Button onClick={onDismissModal} variant='secondary'>
            Cancel
          </Button>
        </ButtonGroup>
      </div>
    </Modal>
  );
}
